/* istanbul ignore file */

import type { FunctionComponent } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import type { HandlesObject, RailObject, TracksObject } from 'react-compound-slider';
import { Handles, Rail, Slider, Tracks } from 'react-compound-slider';

import { RangeInputHandles } from './RangeInputHandles';
import type { RangeInputRefMap } from './RangeInputTextFields';
import { RangeInputTextFieldName, RangeInputTextFields } from './RangeInputTextFields';
import { RangeInputTracks } from './RangeInputTracks';
import { RangeInputRail, RangeInputWrapper, SliderWithStyles } from './styles';
import type {
  AccessibleHandleLabels,
  HandleInputNames,
  RangeInputRanges,
  RangeInputValues,
} from './types';

export interface RangeInputProps {
  accessibleHandleLabels: AccessibleHandleLabels;
  handleInputNames: HandleInputNames;
  range: RangeInputRanges;
  step?: number;
  values: RangeInputValues;
  valueTextPrefix?: string;
  onChange: (value: RangeInputValues) => void;
}

type SliderOnChange = (values: ReadonlyArray<number>) => void;
type SliderOnUpdate = (values: ReadonlyArray<number>) => void;

const SLIDER_MODE = 2;

export const RangeInput: FunctionComponent<RangeInputProps> = ({
  accessibleHandleLabels,
  handleInputNames,
  onChange,
  range,
  step = 1,
  values: initialValues,
  valueTextPrefix,
}: RangeInputProps) => {
  const inputElementsRef = useRef<RangeInputRefMap>({
    [RangeInputTextFieldName.Minimum]: [],
    [RangeInputTextFieldName.Maximum]: [],
  });

  const [values, setValues] = useState<ReadonlyArray<number>>(initialValues);
  const onUpdate = (newValues: ReadonlyArray<number>) => setValues(newValues);

  // When using the reset all button ensure range input re renders with new values
  useEffect(() => {
    setValues(initialValues);
  }, [initialValues]);

  return (
    <RangeInputWrapper>
      <SliderWithStyles
        as={Slider}
        domain={range}
        mode={SLIDER_MODE}
        onUpdate={onUpdate as SliderOnUpdate}
        onChange={onChange as SliderOnChange}
        step={step}
        values={values}
      >
        <Rail>{({ getRailProps }: RailObject) => <RangeInputRail {...getRailProps()} />}</Rail>
        <Handles>
          {({ handles, getHandleProps }: HandlesObject) => (
            <RangeInputHandles
              accessibleHandleLabels={accessibleHandleLabels}
              domain={range}
              handles={handles}
              inputTextFieldRefs={inputElementsRef}
              valueTextPrefix={valueTextPrefix}
              getHandleProps={getHandleProps}
            />
          )}
        </Handles>
        <Tracks left={false} right={false}>
          {({ tracks, getTrackProps }: TracksObject) => (
            <RangeInputTracks tracks={tracks} getTrackProps={getTrackProps} />
          )}
        </Tracks>
      </SliderWithStyles>
      <Slider
        domain={range}
        mode={SLIDER_MODE}
        onChange={onChange as SliderOnChange}
        step={step}
        values={values}
      >
        <Handles>
          {({ handles }: HandlesObject) => (
            <RangeInputTextFields
              ref={inputElementsRef}
              handleInputNames={handleInputNames}
              handles={handles}
              values={values as RangeInputValues}
              valueTextPrefix={valueTextPrefix}
              onChange={onChange}
              range={range}
            />
          )}
        </Handles>
      </Slider>
    </RangeInputWrapper>
  );
};
